import { usePage } from "@inertiajs/vue3";
import { computed } from "vue";
import axios from "axios";
import { isIos } from "@/Helpers/capacitor.js";
import { decodeBase64 } from "@/Helpers/textUtils.js";
import { ExternalPurchaseNative } from "@/Helpers/externalPurchaseNative.js";
import {
    clearPendingRecord,
    createPendingRecord,
    findPendingRecords,
    submitPending,
    sweepPending,
} from "@/Helpers/externalPurchaseQueue.js";

export const useExternalPurchaseEligible = () => computed(() => isIos());

export const initializeExternalPurchase = async () => {
    try {
        const { available } = await ExternalPurchaseNative.canPresent();

        return !!available;
    } catch {
        return false;
    }
};

const decodeExternalPurchaseId = (token) => {
    try {
        let base64 = token.replace(/-/g, "+").replace(/_/g, "/");
        while (base64.length % 4 !== 0) {
            base64 += "=";
        }

        return JSON.parse(decodeBase64(base64))?.externalPurchaseId ?? null;
    } catch {
        return null;
    }
};

export const beginExternalPurchase = async () => {
    try {
        const { cancelled, token } =
            await ExternalPurchaseNative.presentNoticeSheet();

        if (cancelled || !token) {
            return { accepted: false };
        }

        return {
            accepted: true,
            token,
            externalPurchaseId: decodeExternalPurchaseId(token),
        };
    } catch {
        return { accepted: false };
    }
};

export const persistExternalPurchase = ({
    externalPurchaseId,
    productId,
    uniqueIdentifier,
}) => {
    if (!externalPurchaseId || !uniqueIdentifier) {
        return;
    }

    return createPendingRecord({
        uniqueIdentifier,
        productId,
        externalPurchaseId,
    });
};

export const completeExternalPurchase = async (data) => {
    await persistExternalPurchase(data);
    await submitPending(data.uniqueIdentifier);
};

export const cancelExternalPurchase = async ({
    uniqueIdentifier,
    externalPurchaseId,
}) => {
    await clearPendingRecord(uniqueIdentifier);

    try {
        await axios.post(route("paywall.app_store.cancel_token"), {
            unique_identifier: uniqueIdentifier,
            external_transaction_token: externalPurchaseId,
        });
    } catch {}
};

export const resumePendingExternalPurchases = () => sweepPending();

export const resumeIncompleteExternalPurchase = async () => {
    const records = await findPendingRecords();
    if (!records.length) {
        return;
    }

    if (usePage().props.flash?.banner === "PaymentFailedModal") {
        for (const record of records) {
            await cancelExternalPurchase({
                uniqueIdentifier: record.uniqueIdentifier,
                externalPurchaseId: record.externalPurchaseId,
            });
        }

        return;
    }

    await sweepPending();
};
